import * as c from '@iamo/custody-lib';
import { TransactionObject } from 'web3/eth/types';
import { IConnection } from './Connection';
import { IEnrichedTransactionParameters } from './Transaction';

export interface ISafeLimit {
    limit: string;
    period: string;
    currentPeriod?: string;
    spent?: string;
}

/**
 * The limit-class of the wallet
 */
export class Limit {

    // The blockchain property of the backend connection object.
    connection: IConnection

    /**
     * initializes the class
     * @param connection The backend connection object.
     */
    constructor(connection: IConnection){
        this.connection = connection
    }

    /**
     * Returns transaction parameters to change the a limit for a token
     * @param multisigInstanceAddress The address of the multisig instance. 
     * @param limitModuleInstanceAddress The address of the module instance.
     * @param index The array index of the limit.
     * @param newAmount The new allowance.
     * @param token The ERC20 token contract address or the zero address for eth (default: eth).
     * @returns The created transaction parameters
     */
    async changeLimit(
        multisigInstanceAddress: string,
        limitModuleInstanceAddress: string,
        index: number,
        newAmount: string,
        token?: string
    ): Promise<IEnrichedTransactionParameters> {
    
        return c.Limits.changeLimit(this.connection, multisigInstanceAddress, limitModuleInstanceAddress, index, newAmount, token)
    }

    /**
     * Returns transaction parameters to add a limit to a token
     * @param limitModuleInstanceAddress The address of the module instance.
     * @param multisigInstanceAddress The address of the multisig instance.
     * @param limit The limit object.
     * @param token The ERC20 token contract address or the zero address for eth (default: eth).
     * @returns The transaction parameters to add a limit to a token
     */
    async addLimit(
        limitModuleInstanceAddress: string,
        multisigInstanceAddress: string,
        limit: ISafeLimit,
        token?: string
    ): Promise<IEnrichedTransactionParameters> {
        return c.Limits.addLimit(this.connection, limitModuleInstanceAddress, multisigInstanceAddress, limit, token)
    }

    /**
     * Returns the max number of limits allowed per token
     * @param limitModuleInstanceAddress The address of the module instance
     * @returns the max number of limits allowed per token
     */
    async getMaxLimitNumber(
        limitModuleInstanceAddress: string,
    ): Promise<string> {
        return c.Limits.getMaxLimitNumber(this.connection, limitModuleInstanceAddress)
    }

    /**
     * Returns an array of Limits
     * @param limitModuleInstanceAddress The address of the module instance.
     * @param token The ERC20 token contract address or the zero address for eth (default: eth).
     * @returns An array of limits
     */
    async getLimits(
        limitModuleInstanceAddress: string,
        token?: string
    ): Promise<ISafeLimit[]> {
        return c.Limits.getLimits(this.connection, limitModuleInstanceAddress, token)
    }

    /**
     * Returns transaction parameters to delete a limit from a token
     * @param limitModuleInstanceAddress The address of the module instance.
     * @param multisigInstanceAddress The address of the multisig instance.
     * @param index The array index of the limit.
     * @param token The ERC20 token contract address or the zero address for eth (default: eth).
     * @returns The transaction parameters to delete a limit from a token 
     */
    async deleteLimit(
        limitModuleInstanceAddress: string,
        multisigInstanceAddress: string,
        index: number,
        token?: string
    ): Promise<IEnrichedTransactionParameters> {
        return c.Limits.deleteLimit(this.connection, limitModuleInstanceAddress, multisigInstanceAddress, index, token)
    }

    /**
     * Returns if the amount is under the limit for a token
     * @param limitModuleInstanceAddress The address of the module instance.
     * @param to The address of the receiving account.
     * @param amount The amount to transfer
     * @param token The ERC20 token contract address or the zero address for eth (default: eth).
     * @returns True if the amount is under the limit
     */
    async isUnderLimits(
        limitModuleInstanceAddress: string,
        to: string,
        amount: string,
        token?: string
    ): Promise<boolean> {
        return c.Limits.isUnderLimits(this.connection, limitModuleInstanceAddress, to, amount, token)
    }

    /**
     * Sends a token from the default account if the amount is under the limit.
     * @param limitModuleInstanceAddress The address of the module instance.
     * @param to The address of the receiving account.
     * @param amount The amount to transfer.
     * @param token The ERC20 token contract address or the zero address for eth (default: eth).
     * @returns  The transaction parameters to send a transaction under limit
     */
    async sendUnderLimit(
        limitModuleInstanceAddress: string,
        to: string,
        amount: string,
        token?: string
    ): Promise<TransactionObject<any>> {
        return c.Limits.sendUnderLimit(this.connection, limitModuleInstanceAddress, to, amount, token)
    }

    /**
     *  Check limit usage of wallet address based on the contollerWaleltCustodyModule
     * @param controllerWalletCustodyModuleAddress The address of the module instance.
     * @param walletAddress The multisig wallet address
     * @param amount The amount to transfer.
     * @param nonce The transaction nonce of wallet
     * @param token The ERC20 token contract address or the zero address for eth (default: eth).
     * @returns true if the transaction would pass
     */
    async checkLimitUsage(
        controllerWalletCustodyModuleAddress: string,
        walletAddress: string,
        amount: string,
        nonce: string,
        token?: string,
    ): Promise<boolean> {
        return c.Limits.checkLimitUsage(this.connection, controllerWalletCustodyModuleAddress, walletAddress, amount, nonce, token)
    }

}

export default Limit